import { Badge, Button, Col, Flex, message, Row, Table, theme, Typography, Tabs } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowPathIcon, CloudArrowUpIcon, PencilIcon } from '@heroicons/react/24/outline';
import { CustomerRow, KycVerification, useCustomers } from '../useCustomers';
import { useBackoffice } from '../../backoffice/useBackoffice';
import { useOnboardWithFuse } from './useOnboardWithFuse';
import { FuseOnbaordingStatusDetails, useFuseDetails } from './useFuseDetails';
import { css } from '@emotion/css';
import { EditKYCVerificationModal } from './EditKYCVerificationModal';
import { useState } from 'react';
import { CopyOutlined } from '@ant-design/icons';
import { upperCaseFirstLetter } from '../../libs/string';
import { EditCustomerModal } from './EditCustomerModal';
import { ExternalAccountsView } from './CustomerPage.ExternalAccounts';
import { useOnboardWithBridge } from './useOnboardWithBridge';
import { BridgOnbaordingStatusDetails, useBridgeDetails } from './useBridgeDetails';
import { useQueryClient } from '@tanstack/react-query';
import { DocumentsSection } from './CustomerPage.DocumentsSection';

const { Column } = Table;

const CustomerPage = () => {
  const navigate = useNavigate();
  const customersQuery = useCustomers();
  const { id } = useParams();
  const { isBackofficeOn } = useBackoffice();
  const customer: CustomerRow = (customersQuery?.data?.items || []).find((c: CustomerRow) => c.id === id);
  const onboardWithFuseMutation = useOnboardWithFuse();
  const onboardWithBridgeMutation = useOnboardWithBridge();
  const [messageApi, contextHolder] = message.useMessage();
  const useFuseDetailsQuery = useFuseDetails(id!);
  const useBridgeDetailsQuery = useBridgeDetails(id!);
  const [kycVerificationToEdit, setKycVerificationToEdit] = useState<KycVerification | null>(null);
  const { token } = theme.useToken();
  const queryClient = useQueryClient();
  const [editModalOpened, setEditModalOpened] = useState<boolean>(false);

  const renderCustomerDetails = () => {
    if (!customer) {
      return null;
    }

    const source = [
      {
        key: 'ID',
        value: customer.id,
      },
      {
        key: 'Name',
        value: customer.company_name ? customer.company_name : `${customer.first_name} ${customer.last_name}`,
      },
      {
        key: 'Email',
        value: customer.email,
      },
      {
        key: 'Country',
        value: `${customer.address?.country || ''} ${
          customer.address?.country_alpha_2 ? `(${customer.address?.country_alpha_2})` : ''
        }`,
      },
      {
        key: 'Address Line',
        value: customer.address?.address_line_1,
      },
      {
        key: 'City',
        value: customer.address?.city,
      },
      {
        key: 'State',
        value: customer.address?.state,
      },
      {
        key: 'Postal Code',
        value: customer.address?.postal_code,
      },
    ];

    if (isBackofficeOn) {
      source.push(
        {
          key: 'Date Of Birth',
          value: customer.identifying_information?.dob,
        },
        {
          key: 'Tax Number',
          value: customer.identifying_information?.tax_number,
        },
        {
          key: 'ID Document',
          value: `# ${customer.identifying_information?.id_document?.number || 'N/A'} - ${
            customer.identifying_information?.id_document?.issuing_country || ''
          }`,
        }
      );
    }

    return (
      <Table
        title={() => {
          return (
            <Flex style={{ paddingTop: '24px', paddingBottom: '10px' }} justify="space-between" align="center">
              <Typography.Title style={{ margin: 0 }} level={4}>
                Details
              </Typography.Title>

              {isBackofficeOn && (
                <Flex>
                  <Button
                    icon={<PencilIcon width={12} />}
                    onClick={() => {
                      setEditModalOpened(true);
                    }}
                  >
                    Edit Customer
                  </Button>
                </Flex>
              )}
            </Flex>
          );
        }}
        showHeader={false}
        pagination={false}
        dataSource={source}
      >
        <Column
          width={260}
          key="key"
          render={(v) => {
            return v.key;
          }}
        />

        <Column
          key="value"
          render={(v) => {
            const subHeaderStyles = css`
              color: ${token.colorTextSecondary};
            `;
            if (v.key === 'ID') {
              return (
                <Flex align="center">
                  {v.value}
                  <Button
                    type="text"
                    className={subHeaderStyles}
                    size="small"
                    icon={<CopyOutlined width={16} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(v.value);
                      messageApi.success('ID copied to clipboard');
                    }}
                  />
                </Flex>
              );
            }

            return v.value;
          }}
        />
      </Table>
    );
  };

  const renderFusePanel = () => {
    if (!isBackofficeOn) {
      return null;
    }

    return (
      <Table
        loading={useFuseDetailsQuery.isFetching || useFuseDetailsQuery.isPending}
        title={() => {
          return (
            <Flex style={{ paddingTop: '24px', paddingBottom: '10px' }} justify="space-between" align="center">
              <Typography.Title style={{ margin: 0 }} level={4}>
                Fuse
              </Typography.Title>

              <Flex>
                <Button
                  type="text"
                  icon={<ArrowPathIcon width={14} />}
                  onClick={() => {
                    useFuseDetailsQuery.refetch();
                  }}
                ></Button>

                <Button
                  type="text"
                  icon={<CloudArrowUpIcon width={16} />}
                  onClick={() => {
                    onboardWithFuseMutation
                      .mutateAsync({
                        customerId: id!,
                      })
                      .then(() => {
                        messageApi.success('Triggered Fuse onboarding! Wait until status will be updated!');
                      })
                      .catch(() => {
                        messageApi.error('Oops!');
                      });
                  }}
                ></Button>
              </Flex>
            </Flex>
          );
        }}
        showHeader={false}
        pagination={false}
        dataSource={[
          {
            key: 'Onboarding Status',
            value: useFuseDetailsQuery?.data?.onboarding,
          },
        ]}
      >
        <Column
          width={260}
          key="key"
          render={(v) => {
            return v.key;
          }}
        />

        <Column
          key="value"
          render={(v) => {
            const onboarding = v.value as { status_details: FuseOnbaordingStatusDetails };

            if (!onboarding) {
              return 'N/A';
            }

            return (
              <Flex vertical gap={'small'}>
                {onboarding.status_details.entities.map((e) => (
                  <Flex gap={'small'}>
                    <Badge status={e.status === 'success' ? 'success' : 'warning'} /> {e.entity}
                  </Flex>
                ))}
              </Flex>
            );
          }}
        />
      </Table>
    );
  };

  const renderKYCDetails = () => {
    if (!isBackofficeOn) {
      return null;
    }

    if (!customer.kyc_verifications) {
      return null;
    }

    const kyc_verifications = customer.kyc_verifications.filter((v) => v.type);

    return (
      <Table
        title={() => {
          return (
            <Flex style={{ paddingTop: '24px', paddingBottom: '10px' }} justify="space-between" align="center">
              <Typography.Title style={{ margin: 0 }} level={4}>
                KYC
              </Typography.Title>
            </Flex>
          );
        }}
        showHeader={false}
        pagination={false}
        dataSource={kyc_verifications}
      >
        <Column
          width={260}
          key="type"
          render={(v: KycVerification) => {
            return v.type?.[0]?.toUpperCase() + v?.type?.slice(1);
          }}
        />

        <Column
          key="status"
          render={(v: KycVerification) => {
            return (
              <Badge
                text={v?.kyc_status[0].toUpperCase() + v?.kyc_status.slice(1)}
                status={v?.kyc_status === 'approved' ? 'success' : 'warning'}
              />
            );
          }}
        />

        <Column
          key="Actions"
          className={css`
            text-align: right;
          `}
          render={(v: KycVerification) => {
            return (
              <Button
                type="text"
                icon={<PencilIcon width={16} />}
                onClick={() => {
                  setKycVerificationToEdit(v);
                }}
              ></Button>
            );
          }}
        />
      </Table>
    );
  };

  const renderBridgeDetails = () => {
    if (!isBackofficeOn) {
      return null;
    }

    return (
      <Table
        title={() => {
          return (
            <Flex style={{ paddingTop: '24px', paddingBottom: '10px' }} justify="space-between" align="center">
              <Typography.Title style={{ margin: 0 }} level={4}>
                Bridge
              </Typography.Title>

              <Flex>
                <Button
                  type="text"
                  icon={<ArrowPathIcon width={14} />}
                  onClick={() => {
                    useBridgeDetailsQuery.refetch().then(() => {
                      queryClient.invalidateQueries({
                        queryKey: ['customers'],
                      });
                    });
                  }}
                ></Button>

                <Button
                  type="text"
                  icon={<CloudArrowUpIcon width={16} />}
                  onClick={() => {
                    onboardWithBridgeMutation
                      .mutateAsync({
                        customerId: id!,
                      })
                      .then(() => {
                        messageApi.success('Triggered Bridge onboarding! Wait until status will be updated!');
                      })
                      .catch(() => {
                        messageApi.error('Oops!');
                      });
                  }}
                ></Button>
              </Flex>
            </Flex>
          );
        }}
        showHeader={false}
        pagination={false}
        dataSource={[
          {
            key: 'Customer ID',
            value: customer.money_movement_settings?.implementer_contexts?.bridge?.context.customerId,
          },
          {
            key: 'Onboarding Status',
            value: useBridgeDetailsQuery?.data?.onboarding,
          },
        ]}
      >
        <Column
          width={260}
          key="key"
          render={(v) => {
            return v.key;
          }}
        />

        <Column
          key="value"
          render={(v) => {
            if (!v.value) {
              return 'N/A';
            }
            const subHeaderStyles = css`
              color: ${token.colorTextSecondary};
            `;

            if (v.key === 'Onboarding Status') {
              const onboarding = v.value as { status_details: BridgOnbaordingStatusDetails };

              if (!onboarding) {
                return 'N/A';
              }
              return (
                <Flex vertical gap={'small'}>
                  {onboarding.status_details.entities.map((e) => (
                    <Flex gap={'small'}>
                      <Badge status={e.status === 'success' ? 'success' : 'warning'} /> {e.entity}
                    </Flex>
                  ))}
                </Flex>
              );
            }

            return (
              <Flex align="center">
                {v.value}
                <Button
                  type="text"
                  className={subHeaderStyles}
                  size="small"
                  icon={<CopyOutlined width={16} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(v.value);
                    messageApi.success('ID copied to clipboard');
                  }}
                />
              </Flex>
            );
          }}
        />
      </Table>
    );
  };

  const renderFeesSettingsDetails = () => {
    if (!isBackofficeOn) {
      return null;
    }

    return (
      <Table
        title={() => {
          return (
            <Flex style={{ paddingTop: '24px', paddingBottom: '10px' }} justify="space-between" align="center">
              <Typography.Title style={{ margin: 0 }} level={4}>
                Fees and Settings
              </Typography.Title>
            </Flex>
          );
        }}
        showHeader={false}
        pagination={false}
        dataSource={[
          {
            key: 'Enabled Api Corridors',
            value: customer.money_movement_settings?.core_api?.enabled_corridors
              .map((c) => upperCaseFirstLetter(c))
              .join(', '),
          },
          {
            key: 'Align Fees',
            value: `Offramp - ${customer.money_movement_settings?.fees?.offramp?.percentage ?? 'N/A'} %, Onramp - ${
              customer.money_movement_settings?.fees?.onramp?.percentage ?? 'N/A'
            } %, Cross Chain - ${customer.money_movement_settings?.fees?.cross_chain_transfer?.percentage ?? 'N/A'} %`,
          },
        ]}
      >
        <Column
          width={260}
          key="key"
          render={(v) => {
            return v.key;
          }}
        />

        <Column
          key="value"
          render={(v) => {
            if (!v.value) {
              return 'N/A';
            }

            return v.value;
          }}
        />
      </Table>
    );
  };

  if (!customer || !id) {
    return null;
  }

  return (
    <>
      {contextHolder}

      <Row justify="center">
        <Col xs={24} sm={24} md={20} lg={16} xl={14}>
          <Button
            style={{ marginBottom: '22px' }}
            icon={<ArrowLeftIcon width={14} />}
            type="text"
            onClick={() => {
              navigate(-1);
            }}
          >
            All Customers
          </Button>
          <Tabs
            type="card"
            items={[
              {
                label: `Details`,
                key: 'details',
                children: (
                  <>
                    <Flex vertical gap={'large'}>
                      {renderCustomerDetails()}
                      {renderFeesSettingsDetails()}

                      {renderKYCDetails()}

                      <DocumentsSection customerId={id} />

                      {renderBridgeDetails()}

                      {renderFusePanel()}
                    </Flex>
                  </>
                ),
              },
              {
                label: `External Accounts`,
                key: 'external-accounts',
                children: <ExternalAccountsView customerId={id} />,
              },
            ]}
          />
        </Col>
      </Row>

      {kycVerificationToEdit && (
        <EditKYCVerificationModal
          customerId={id!}
          open={!!kycVerificationToEdit}
          onClose={() => {
            setKycVerificationToEdit(null);
          }}
          kycVerification={kycVerificationToEdit}
        />
      )}

      {editModalOpened && (
        <EditCustomerModal
          customer={customer}
          open
          onClose={() => {
            setEditModalOpened(false);
          }}
        />
      )}
    </>
  );
};

export default CustomerPage;
